import React from 'react';

import { Box, IconButton, Typography, Chip, Popover } from '@mui/material';

import { HighlightOff, AddCircleOutline } from '@mui/icons-material';
import { useTheme } from '@emotion/react';
import { TagsContext } from 'Modules/Components/TagsContext';

export default function TagEditor({ selectedTags, updateTags  }) {
    const [showUnassigned, setShowUnasigned] = React.useState(false);
    const [activeCategory, setActiveCategory] = React.useState('');

    const [visibleTags, setVisibleTags] = React.useState({});
    const [hiddenTags, setHiddenTags] = React.useState({});
    const [anchorEl, setAnchorEl] = React.useState(null);
    const theme = useTheme();
    const { tagList } = React.useContext(TagsContext);

    React.useEffect(() => {
        const v = {};
        const h = {};
        Object.keys(tagList).forEach((category) => {
            if (category === 'Milestones') return;
            v[category] = selectedTags?.[category]?.map((id) => tagList[category].find((t) => t.id === id));
            h[category] = tagList?.[category].filter(
                (mt) => !selectedTags?.[category]?.find((t) => mt.id === t)
            );
        });
        setVisibleTags(v);
        setHiddenTags(h);
    }, [selectedTags, tagList]);

    const toggleTagPicker = (event, category) => {
        if (!showUnassigned) {
            setActiveCategory(category);
            setShowUnasigned(true);
            setAnchorEl(event.currentTarget);
        } else {
            setActiveCategory(null);
            setShowUnasigned(false);
            setAnchorEl(null);
        }
    };

    const addTagClicked = (category, id) => {
        const list = selectedTags?.[category] || [];
        const newTags = { ...selectedTags, [category]: [...list, id] };
        updateTags(newTags);
    };

    const removeTagClicked = (category, id) => {
        const list = selectedTags?.[category] || [];
        const newTags = {
            ...selectedTags,
            [category]: list.filter((t) => t !== id),
        };
        updateTags(newTags);
    };

    return (
        <Box flex="1">
            <Box flexDirection="row" display="flex">
                {Object.keys(visibleTags).map((category) => (
                    <Box key={category} flex="1" flexDirection="column" display="flex" mr={2}>
                        <Box display="flex" flexDirection="row" justifyContent="flex-start">
                            <IconButton
                                aria-label="map"
                                size="small"
                                onClick={(event) => toggleTagPicker(event, category)}
                                style={{ fontSize: '28px' }}
                            >
                                {activeCategory === category && showUnassigned ? (
                                    <HighlightOff size="small" style={{ color: '#300' }} />
                                ) : (
                                    <AddCircleOutline size="small" style={{ color: '#009' }} />
                                )}
                                <Typography
                                    variant="overline"
                                    sx={{ pl: 1 }}
                                    style={{
                                        fontSize: '12px',
                                        fontWeight: 400,
                                        letterSpacing: 0,
                                        lineHeight: '38px',
                                        color: theme.palette.text.primary,
                                        fontFamily: theme.typography.fontFamily
                                    }}
                                >
                                    {category}
                                </Typography>
                            </IconButton>
                        </Box>
                        <Box display="flex" flexDirection="row" justifyContent="flex-start" mr={2} flexWrap="wrap">
                            {visibleTags[category] ? (
                                visibleTags[category]?.map((t = {}) => (
                                    <Chip
                                        key={category + '_' + t.id}
                                        size="medium"
                                        label={t.name}
                                        color="button"
                                        style={{
                                            padding: '6px',
                                            marginRight: '6px',
                                            marginBottom: '6px',
                                            display: t.name ? 'block':'none'
                                        }}
                                        onDelete={
                                            activeCategory === category && showUnassigned
                                                ? () => removeTagClicked(category, t.id)
                                                : null
                                        }
                                    />
                                ))
                            ) : (
                                <Typography variant="button" gutterBottom>
                                    {' '}
                                    None Assigned{' '}
                                </Typography>
                            )}
                        </Box>
                        <Popover
                            open={activeCategory === category && showUnassigned}
                            anchorEl={anchorEl}
                            onClose={() => toggleTagPicker(null, null)}
                            PaperProps={{
                                style: { width: 330, padding: '8px' },
                            }}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left',
                            }}
                        >
                            {activeCategory === category && showUnassigned && (
                                <>
                                    <Box
                                        display="flex"
                                        flexDirection="row"
                                        justifyContent="flex-start"
                                        mr={2}
                                        flexWrap="wrap"
                                    >
                                        {visibleTags[category]
                                            ? visibleTags[category]?.map((t) => (
                                                  <Chip
                                                      key={category + '_' + t.id}
                                                      size="medium"
                                                      label={t.name}
                                                      color="button"
                                                      style={{
                                                          padding: '2px 6px',
                                                          marginRight: '6px',
                                                          marginBottom: '6px',
                                                      }}
                                                      onDelete={
                                                          activeCategory === category && showUnassigned
                                                              ? () => removeTagClicked(category, t.id)
                                                              : null
                                                      }
                                                  />
                                              ))
                                            : null}
                                        {hiddenTags[category] ? (
                                            hiddenTags[category]?.map((t) => (
                                                <Chip
                                                    key={category + '_' + t.id}
                                                    size="medium"
                                                    variant="outlined"
                                                    label={t.name}
                                                    clickable={true}
                                                    icon={<AddCircleOutline />}
                                                    onClick={() => {
                                                        addTagClicked(category, t.id);
                                                    }}
                                                />
                                            ))
                                        ) : (
                                            <Typography variant="button" gutterBottom>
                                                {' '}
                                                None Available{' '}
                                            </Typography>
                                        )}
                                    </Box>
                                </>
                            )}
                        </Popover>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}
